import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCATION_IMAGE = "https://images.pexels.com/photos/36579567/pexels-photo-36579567.jpeg";

const Contatti = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Messaggio inviato con successo!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Errore nell'invio del messaggio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-400 mb-4 block">
              Contatti
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              Siamo qui per te
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Hai domande o hai bisogno di informazioni? Contattaci tramite il form, per telefono o vieni a trovarci in ufficio.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-2xl p-6" data-testid="contact-address">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Indirizzo</h3>
              <p className="text-slate-600">
                Corso Nunzio Ricco n° 9<br />
                76016 Margherita di Savoia (BT)
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6" data-testid="contact-phone">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Telefono</h3>
              <a 
                href="tel:0883411359" 
                className="text-blue-900 font-semibold text-lg hover:text-blue-700 transition-colors"
              >
                0883 411359
              </a>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6" data-testid="contact-email">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Email</h3>
              <a 
                href="mailto:patronatoepasmargherita@gmail.com" 
                className="text-blue-900 font-semibold hover:text-blue-700 transition-colors text-sm break-all"
              >
                patronatoepasmargherita@gmail.com
              </a>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-6" data-testid="contact-hours">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Orari</h3>
              <p className="text-slate-600">
                Lun - Ven: 09:00 - 18:00<br />
                <span className="text-slate-500 text-sm">Pausa: 13:00 - 14:00</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              {success ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                    Messaggio Inviato!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Grazie per averci contattato. Ti risponderemo al più presto.
                  </p>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                    }}
                    variant="outline"
                    className="rounded-full"
                    data-testid="send-another-message"
                  >
                    Invia un altro messaggio
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                    Inviaci un messaggio
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome e Cognome *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Mario Rossi"
                          required
                          className="h-12"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="mario.rossi@email.com"
                          required
                          className="h-12"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefono</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="333 1234567"
                          className="h-12"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Oggetto *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="Richiesta informazioni"
                          required
                          className="h-12"
                          data-testid="contact-input-subject"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Messaggio *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Scrivi qui il tuo messaggio..."
                        rows={6}
                        required
                        data-testid="contact-input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white rounded-full"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        "Invio in corso..."
                      ) : (
                        <>
                          Invia Messaggio
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>

            {/* Map & Image */}
            <div className="space-y-6">
              {/* Map */}
              <div className="map-container rounded-2xl shadow-sm">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3014.8456788901234!2d16.1517622!3d41.3744127!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDIyJzI4LjAiTiAxNsKwMDknMDYuNCJF!5e0!3m2!1sit!2sit!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mappa CAF Patronato EPAS"
                ></iframe>
              </div>

              {/* Location Image */}
              <div className="relative rounded-2xl overflow-hidden h-64">
                <img
                  src={LOCATION_IMAGE}
                  alt="Margherita di Savoia"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-heading text-xl font-bold mb-1">Margherita di Savoia</h3>
                  <p className="text-slate-200 text-sm">La perla della costa adriatica pugliese</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
